import { useEffect, useRef, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { PauseIcon, PlayIcon } from "@hugeicons-pro/core-solid-rounded";
import { toast } from "react-toastify";
import { cx } from "shared/utils";

// The one message playing on the page: starting another stops it.
let playing: { audio: HTMLAudioElement; stop: () => void } | null = null;
// The latest press on any button: one still loading its URL when another is
// pressed gives up instead of playing over it.
let latestPress = 0;
// The API presigns audio for an hour: a URL kept longer is asked for again.
const URL_REUSE_MS = 50 * 60_000;

const stopPlaying = () => {
  playing?.stop();
  playing = null;
};

/**
 * Play / Pause for a pre-recorded voicemail (CRM-1830): the Phone numbers tab,
 * the Calls drawer, the lead's activity log and a fresh recording before it is
 * saved. `loadUrl` resolves the audio URL on the first press and again once
 * the presigned one is close to expiring, or after a press that failed.
 */
export const VoicemailPlayButton = ({
  loadUrl,
  name,
  size = "base"
}: {
  loadUrl: () => Promise<string>;
  // The message's name: screen readers hear "Play First outreach".
  name: string;
  size?: "sm" | "base";
}) => {
  const [state, setState] = useState<"idle" | "loading" | "playing">("idle");
  const urlRef = useRef<{ url: string; at: number } | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(
    () => () => {
      if (playing && playing.audio === audioRef.current) stopPlaying();
    },
    []
  );

  const stop = () => {
    audioRef.current?.pause();
    setState("idle");
  };

  // A fresh URL needs a fresh element: the old one holds the old URL.
  const currentUrl = async () => {
    const cached = urlRef.current;
    if (cached && Date.now() - cached.at < URL_REUSE_MS) return cached.url;
    const url = await loadUrl();
    urlRef.current = { url, at: Date.now() };
    audioRef.current = null;
    return url;
  };

  const play = async () => {
    latestPress += 1;
    const press = latestPress;
    stopPlaying();
    setState("loading");
    try {
      const url = await currentUrl();
      if (press !== latestPress) {
        setState("idle");
        return;
      }
      const audio = audioRef.current ?? new Audio(url);
      audioRef.current = audio;
      audio.currentTime = 0;
      audio.onended = () => setState("idle");
      playing = { audio, stop };
      await audio.play();
      setState("playing");
    } catch {
      // The next press starts over with a new URL.
      urlRef.current = null;
      audioRef.current = null;
      setState("idle");
      toast.error("Couldn't play the message. Try again.");
    }
  };

  const isPlaying = state === "playing";
  return (
    <button
      type="button"
      aria-label={`${isPlaying ? "Pause" : "Play"} ${name}`}
      disabled={state === "loading"}
      onClick={(event) => {
        // Rows that open a drawer on click must not open it from here.
        event.stopPropagation();
        if (isPlaying) stopPlaying();
        else void play();
      }}
      className={cx(
        "grid shrink-0 place-items-center rounded-full bg-alpha-40 text-content-1000 transition hover:bg-alpha-100 disabled:opacity-60",
        size === "sm" ? "size-7" : "size-8"
      )}
    >
      <HugeiconsIcon
        icon={isPlaying ? PauseIcon : PlayIcon}
        size={size === "sm" ? 14 : 16}
      />
    </button>
  );
};
